import { cn, debounce, INPUT_TYPE_DEBOUNCE } from "@istari-frontend/shared";
import { X } from "lucide-react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Input } from "./input";

export interface SearchInputProps extends Omit<React.ComponentProps<typeof Input>, "onChange" | "value"> {
  onChange: (value: string) => void;
  showClearButton?: boolean;
  /**
   * Debounce delay in milliseconds. If provided, onChange will be debounced.
   * Set to 0 or undefined to disable debouncing.
   * @default INPUT_TYPE_DEBOUNCE (400ms)
   */
  debounceMs?: number;
  containerClassName?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      onChange,
      showClearButton = true,
      debounceMs = INPUT_TYPE_DEBOUNCE,
      containerClassName,
      className,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    // Seed the uncontrolled input once so callers can hydrate from URL state (e.g. ?q=) without the
    // visible box going out of sync with the active query. Later prop changes don't re-seed.
    const [value, setValue] = useState(typeof defaultValue === "string" ? defaultValue : "");

    // The debounced callback reads the latest onChange through a ref, so a parent that re-renders with a
    // new handler mid-interval neither resets the timer nor loses the keystroke it was waiting on.
    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const debouncedOnChange = useMemo(
      () => (debounceMs && debounceMs > 0 ? debounce((next: string) => onChangeRef.current(next), debounceMs) : null),
      [debounceMs],
    );
    // A keystroke's pending call must not outlive the input: a layout effect's cleanup runs in the unmount
    // commit itself, before a timer that is already due could fire.
    useLayoutEffect(() => () => debouncedOnChange?.cancel(), [debouncedOnChange]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        if (debouncedOnChange) debouncedOnChange(newValue);
        else onChangeRef.current(newValue);
      },
      [debouncedOnChange],
    );

    const handleClear = useCallback(() => {
      // A keystroke still waiting on the timer would otherwise land after the box is already empty.
      debouncedOnChange?.cancel();
      setValue("");
      onChangeRef.current("");
    }, [debouncedOnChange]);

    return (
      <div className={cn("relative", containerClassName)}>
        <Input
          ref={ref}
          value={value}
          onChange={handleChange}
          autoComplete="off"
          className={cn(showClearButton && value && "pr-8", className)}
          {...props}
        />
        {showClearButton && value && (
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer rounded p-0.5 transition-colors duration-200 ease-out"
            aria-label="Clear search"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";
